import Icon from '@ant-design/icons';
import DeleteOutlined from '@ant-design/icons/lib/icons/DeleteOutlined';
import { Button, Col, Form, FormInstance, Input, message, Row } from 'antd';
import React, { Component } from 'react';
import { FormattedMessage, injectIntl, WrappedComponentProps } from 'react-intl';
import { Link, RouteComponentProps } from 'react-router-dom';
import organizationApi from '../../../../apis/OrganizationApi';
import LayoutComponent from '../../../../components/LayoutComponent/LayoutComponent';
import CustomContext from '../../../../context/CustomContext';
import { Organization } from '../../../../model/Entities';
import { ReactComponent as SaveSvg } from '../../../../resources/images/ico-save.svg';
import notificationService from '../../../../services/NotificationService';
import styles from './OrganizationPage.module.scss';

class OrganizationPage extends Component<Props, State> {
    static contextType = CustomContext;
    context!: React.ContextType<typeof CustomContext>;
    formRef = React.createRef<FormInstance>();

    constructor(props: Props) {
        super(props);
        this.state = {};
    }

    componentDidMount() {
        this.init();
    }

    /** METHODS **/

    init = async () => {
        try {
            this.setState({ loading: true });
            if (this.props.match.params.id === 'new') {
                await this.new();
            } else {
                await this.get(this.props.match.params.id);
            }
        } catch (error) {
        } finally {
            this.setState({ loading: false });
        }
    };

    new = async () => {
        const organization: Organization = {
            name: '',
        };

        this.setState({ organization });
        this.formRef.current!.setFieldsValue(organization);
    };

    get = async (id: string) => {
        const organization = await organizationApi.get(id);

        this.setState({ organization });
        this.formRef.current!.setFieldsValue(organization);
    };

    save = async (values: any) => {
        try {
            this.setState({ loading: true });
            let organization: Organization = Object.assign({}, this.state.organization, values);
            organization = organization.id
                ? await organizationApi.update(organization)
                : await organizationApi.create(organization);
            message.success(this.props.intl.formatMessage({ id: 'status.saved' }));

            this.setState({ organization });
        } catch (error) {
            notificationService.displayError(error, this.props.intl, [
                { status: 409, message: 'organization.status.duplicate' },
            ]);
        } finally {
            this.setState({ loading: false });
        }
    };

    delete = async () => {
        const { organization } = this.state;
        try {
            this.setState({ loading: true });
            await organizationApi.delete(organization!);
            this.props.history.push(`/admin/organizations`);
        } catch (error) {
            notificationService.displayError(error, this.props.intl);
        } finally {
            this.setState({ loading: false });
        }
    };

    /*** COMPONENTS ***/

    renderContent = (): React.ReactElement | undefined => {
        const { loading } = this.state;

        return (
            <>
                {this.renderToolbar()}
                <Form ref={this.formRef} onFinish={this.save} colon={false} layout="vertical">
                    <Row gutter={[28, 0]}>
                        <Col xs={24} md={8}>
                            <Form.Item
                                label={<FormattedMessage id="organization.name" />}
                                name="name"
                                rules={[{ required: true, message: <FormattedMessage id="status.mandatory" /> }]}
                            >
                                <Input maxLength={100} size="large" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                            <Form.Item label={<FormattedMessage id="organization.address" />} name="address">
                                <Input maxLength={100} size="large" />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={[28, 0]}>
                        <Col xs={24} md={8}>
                            <Form.Item label={<FormattedMessage id="organization.city" />} name="city">
                                <Input maxLength={100} size="large" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                            <Form.Item label={<FormattedMessage id="organization.state" />} name="state">
                                <Input maxLength={100} size="large" />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={[28, 0]}>
                        <Col xs={24} md={8}>
                            <Form.Item label={<FormattedMessage id="organization.location" />} name="location">
                                <Input maxLength={100} size="large" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                            <Form.Item label={<FormattedMessage id="organization.zip" />} name="zip">
                                <Input maxLength={100} size="large" />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={[28, 0]}>
                        <Col xs={24} md={8}>
                            <Form.Item label={<FormattedMessage id="organization.population" />} name="population">
                                <Input maxLength={100} size="large" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                            <Form.Item label={<FormattedMessage id="organization.lowestGrade" />} name="lowestGrade">
                                <Input maxLength={100} size="large" />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={[28, 0]}>
                        <Col xs={24} md={8}>
                            <Form.Item label={<FormattedMessage id="organization.highestGrade" />} name="highestGrade">
                                <Input maxLength={100} size="large" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                            <Form.Item label={<FormattedMessage id="organization.athleteCode" />} name="athleteCode">
                                <Input maxLength={100} size="large" />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={[28, 0]}>
                        <Col xs={24} md={8}>
                            <Form.Item label={<FormattedMessage id="organization.coachCode" />} name="coachCode">
                                <Input maxLength={100} size="large" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                            <Form.Item label={<FormattedMessage id="organization.adminCode" />} name="adminCode">
                                <Input maxLength={100} size="large" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <div className={styles.buttons}>
                        <Link to="/admin/organizations">
                            <Button size="large" className={styles.back}>
                                <FormattedMessage id="organization.back" />
                            </Button>
                        </Link>
                        {this.props.match.params.id !== 'new' && (
                            <Button
                                size="large"
                                icon={<DeleteOutlined />}
                                className={styles.delete}
                                onClick={() => this.delete()}
                            >
                                <FormattedMessage id="organization.delete" />
                            </Button>
                        )}
                        <Button
                            type="primary"
                            htmlType="submit"
                            size="large"
                            className={styles.save}
                            icon={<Icon component={SaveSvg} />}
                            loading={loading}
                        >
                            <FormattedMessage id="organization.save" />
                        </Button>
                    </div>
                </Form>
            </>
        );
    };

    renderToolbar = (): React.ReactElement | undefined => {
        if (this.props.match.params.id === 'new') {
            return (
                <div className={styles.toolbar}>
                    <h1>
                        <FormattedMessage id="organizations.addtitle" />
                    </h1>
                </div>
            );
        } else {
            return (
                <div className={styles.toolbar}>
                    <h1>
                        <FormattedMessage id="organizations.edittitle" />
                    </h1>
                </div>
            );
        }
    };

    render() {
        return <LayoutComponent page="add-organization" content={this.renderContent} />;
    }
}
export default injectIntl(OrganizationPage);

type ParamsType = { id: string };
interface Props extends RouteComponentProps<ParamsType>, WrappedComponentProps {}

interface State {
    organization?: Organization;
    loading?: boolean;
}
